import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import { API_CONFIG } from "../../../utils/apiConfig";
import "./WeeklyRevenue.css";

const WeeklyRevenue = () => {
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeeklyBills = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("firebaseToken") || localStorage.getItem("token");

        if (!token) {
          setWeeklyData([
            { week: "This Week", cash: 2, credit: 0, debit: 0, total: 2 },
          ]);
          setLoading(false);
          return;
        }

        const [cashRes, creditRes, debitRes] = await Promise.all([
          axios.get(`${API_CONFIG.BASE_URL}/api/revenue/cashbills/weekly`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_CONFIG.BASE_URL}/api/revenue/creditbills/weekly`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_CONFIG.BASE_URL}/api/revenue/debitnotes/weekly`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const cash = cashRes.data?.length || 0;
        const credit = creditRes.data?.length || 0;
        const debit = debitRes.data?.length || 0;

        setWeeklyData([
          {
            week: "This Week",
            cash,
            credit,
            debit,
            total: cash + credit + debit,
          },
        ]);
      } catch (err) {
        console.error("Weekly data fetch failed:", err);
        setWeeklyData([{ week: "This Week", cash: 2, credit: 0, debit: 0, total: 2 }]);
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklyBills();
  }, []);

  return (
    <div className="revenue-card">
      <h4>📊 Weekly Bills Summary</h4>
      <p className="revenue-subtitle">Cash, Credit, and Debit Bills</p>

      <div className="chartt-container">
        {loading ? (
          <div className="loading-container">
            <p>Loading weekly bills...</p>
          </div>
        ) : weeklyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={weeklyData}
              margin={{ top: 0, right: 30, left: 40, bottom: 15 }}
              barCategoryGap="15%"
              barGap={2}
            >
              <defs>
                <linearGradient id="cashGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00ff88" stopOpacity={0.9} />
                  <stop offset="50%" stopColor="#00cc6a" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#009944" stopOpacity={0.7} />
                </linearGradient>
                <linearGradient id="creditGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00bfff" stopOpacity={0.9} />
                  <stop offset="50%" stopColor="#0080ff" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#0040cc" stopOpacity={0.7} />
                </linearGradient>
                <linearGradient id="debitGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ff4080" stopOpacity={0.9} />
                  <stop offset="50%" stopColor="#cc0040" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#800020" stopOpacity={0.7} />
                </linearGradient>
                <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c0c0c0" stopOpacity={0.9} />
                  <stop offset="50%" stopColor="#808080" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#404040" stopOpacity={0.7} />
                </linearGradient>
                
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <CartesianGrid strokeDasharray="1 1" stroke="#f0f0f0" />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 14, fill: '#666', fontWeight: 500 }}
                axisLine={{ stroke: '#ddd', strokeWidth: 2 }}
                tickLine={{ stroke: '#ddd' }}
              />
              <YAxis
                tick={{ fontSize: 14, fill: '#666', fontWeight: 500 }}
                axisLine={{ stroke: '#ddd', strokeWidth: 2 }}
                tickLine={{ stroke: '#ddd' }}
                label={{
                  value: "Number of Bills",
                  angle: -90,
                  position: "insideLeft",
                  fontSize: 14,
                  dy: 20,
                  style: { textAnchor: 'middle', fill: '#666' }
                }}
              />
              <Tooltip 
                formatter={(value) => [`${value} bills`, 'Count']}
                labelStyle={{ color: '#333', fontWeight: 600 }}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Legend 
                verticalAlign="middle" 
                align="right" 
                layout="vertical"
                iconType="rect"
                iconSize={16}
                wrapperStyle={{ 
                  paddingRight: '10px', 
                  fontSize: '14px',
                  backgroundColor: 'transparent'
                }}
              />

              <Bar 
                dataKey="cash" 
                fill="url(#cashGradient)" 
                name="Cash Bills" 
                radius={[2, 2, 0, 0]}
                barSize={25}
                label={{
                  position: 'center',
                  angle: -90,
                  fill: '#ffffff',
                  fontSize: 10,
                  fontWeight: 700,
                  formatter: () => 'Cash Bills'
                }}
              />

              <Bar 
                dataKey="credit" 
                fill="url(#creditGradient)" 
                name="Credit Bills" 
                radius={[6, 6, 0, 0]}
                barSize={25}
                label={{
                  position: 'center',
                  angle: -90,
                  fill: '#ffffff',
                  fontSize: 10,
                  fontWeight: 700,
                  formatter: () => 'Credit Bills'
                }}
              />

              <Bar 
                dataKey="debit" 
                fill="url(#debitGradient)" 
                name="Debit Notes" 
                radius={[6, 6, 0, 0]}
                barSize={25}
                label={{
                  position: 'center',
                  angle: -90,
                  fill: '#ffffff',
                  fontSize: 10,
                  fontWeight: 700,
                  formatter: () => 'Debit Notes'
                }}
              />

              <Bar 
                dataKey="total" 
                fill="url(#totalGradient)" 
                name="Total Bills" 
                radius={[6, 6, 0, 0]}
                barSize={25}
                label={{
                  position: 'center',
                  angle: -90,
                  fill: '#ffffff',
                  fontSize: 10,
                  fontWeight: 700,
                  formatter: () => 'Total Bills'
                }}
              />

            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="no-data-container">
            <p>No data available this week.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyRevenue;
